import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { getHeaders } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { ChevronDown } from "lucide-react";

interface RoomType {
  id?: string;
  name: string;
  code?: string;
  [key: string]: any;
}

interface RoomCategoriesCardProps {
  propertyId: string;
  currentIntegration?: any | null;
}

const RoomCategoriesCard: React.FC<RoomCategoriesCardProps> = ({ propertyId, currentIntegration }) => {
  const { toast } = useToast();
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(false);
  const [populating, setPopulating] = useState(false);

  const fetchRoomTypes = async () => {
    try {
      setLoading(true);
      const headers = await getHeaders();

      // API expects a POST with propertyId and a hard-coded categoryClass
      const body = JSON.stringify({ propertyId, categoryClass: "Accomodation" });

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/pms/roomTypes`, {
        method: "POST",
        mode: "cors",
        headers,
        body,
      });

      if (!resp.ok) {
        throw new Error(`Failed to load room types from DB: ${resp.status}`);
      }

      const data = await resp.json();
      if (Array.isArray(data)) {
        setRoomTypes(data);
      } else if (data && data.roomTypes) {
        setRoomTypes(data.roomTypes);
      } else if (data && data.body && Array.isArray(data.body)) {
        setRoomTypes(data.body);
      } else if (data && data.data && Array.isArray(data.data)) {
        // some APIs return { data: [...] }
        setRoomTypes(data.data);
      } else {
        setRoomTypes([]);
      }

      toast({ title: "Room types loaded from DB" });
    } catch (error) {
      console.error("Error fetching room types from DB", error);
      toast({ title: "Error fetching room types", description: String(error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePopulate = async () => {
    if (!currentIntegration) {
      toast({ title: "No PMS integration", description: "Connect a PMS integration before initiating room types.", variant: "destructive" });
      return;
    }

    try {
      setPopulating(true);
      const headers = await getHeaders();

      const integrationId = currentIntegration.integrationId || currentIntegration.id || null;
      const providerName = (currentIntegration.name || currentIntegration.provider || "").toString().toLowerCase().replace(/\s+/g, "");

      const body = JSON.stringify({ propertyId, integrationId, pmsProvider: providerName });

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/pms/initiateRoomTypes`, {
        method: "POST",
        mode: "cors",
        headers,
        body,
      });

      if (!resp.ok) {
        const errBody = await resp.text();
        throw new Error(errBody || `Status ${resp.status}`);
      }

      const data = await resp.json();
      if (Array.isArray(data)) {
        setRoomTypes(data);
      } else if (data && data.roomTypes) {
        setRoomTypes(data.roomTypes);
      } else if (data && data.body && Array.isArray(data.body)) {
        setRoomTypes(data.body);
      } else if (data && data.data && Array.isArray(data.data)) {
        setRoomTypes(data.data);
      }

      toast({ title: "Initiated room types", description: "Room types retrieved from PMS" });
    } catch (error) {
      console.error("Error initiating room types", error);
      toast({ title: "Error initiating room types", description: String(error), variant: "destructive" });
    } finally {
      setPopulating(false);
    }
  };

  return (
    <Card>
      {/* <Collapsible defaultOpen={true}> */}
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Room Categories</span>
            {/* <CollapsibleTrigger asChild>
              <Button size="sm" variant="ghost">
                <ChevronDown className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger> */}
          </CardTitle>
          <CardDescription>View and import room categories from your PMS</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <div className="flex space-x-2">
              <Button size="sm" onClick={fetchRoomTypes} disabled={loading}>
                {loading ? "Loading..." : "Fetch Room Types"}
              </Button>
              {currentIntegration ? (
                <Button size="sm" onClick={handlePopulate} disabled={populating}>
                  {populating ? "Populating..." : "Populate Room Types"}
                </Button>
              ) : (
                <Button size="sm" variant="outline" disabled>
                  No PMS Integration
                </Button>
              )}
            </div>
          </div>

          {/* <CollapsibleContent> */}
            {roomTypes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No room categories loaded.</p>
            ) : (
              <div className="space-y-2">
                <ul className="list-disc pl-5">
                  {roomTypes.map((rt, idx) => (
                    <li key={rt.id || rt.code || idx} className="text-sm">
                      {rt.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          {/* </CollapsibleContent> */}
        </CardContent>
      {/* </Collapsible> */}
    </Card>
  );
};

export default RoomCategoriesCard;
